import React, { useEffect, useRef, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  LayersControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import Select from "react-select";
import { getDataSetNamePBI, useHttpClient } from "../modules/services/Bundle";
import { IAtivosModel } from "../modules/services/core/_models";
import { RequestMethod } from "../modules/services/core/_enums";
import { useLocation } from "react-router-dom";
import { CSSObject } from "@emotion/react";

const osmLayer = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const satelliteLayer =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const darkLayer =
  "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";

// Correção dos ícones padrão do Leaflet
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png",
});

const FitBounds = ({ bounds }: { bounds: L.LatLngBoundsExpression }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds);
    }
  }, [bounds, map]);
  return null;
};

const AtivoMapPage: React.FC = () => {
  const location = useLocation();
  const httpClient = useHttpClient();
  const [listaAtivo, setListaAtivo] = useState<IAtivosModel[]>([]);
  const [filteredData, setFilteredData] = useState<IAtivosModel[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<string[]>(
    []
  );
  const [selectedAtivo, setSelectedAtivo] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const mapRef = useRef<L.Map>(null);

  const dataSetNamePBI = getDataSetNamePBI();

  const obterListaAtivo = async () => {
    try {
      const response = await httpClient.request({
        method: RequestMethod.GET,
        endpoint: "/Gerenciamento/PowerBI/ObterAtivos",
        queryObject: { datasetName: dataSetNamePBI },
      });

      if (response.success && response.payload) {
        const validData = response.payload.filter(
          (ativo: IAtivosModel) =>
            ativo.Latitude !== null &&
            ativo.Longitude !== null &&
            ativo.Latitude !== undefined &&
            ativo.Longitude !== undefined
        );
        validData.forEach((item: IAtivosModel) => {
          if (typeof item.UltimaVisualizacao === 'string') {

            const match = item.UltimaVisualizacao.match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/)
            if(match) {
              const [_, day, month, year, hours, mins, secs] = match;
              const date = new Date(
                parseInt(year),
                parseInt(month) - 1,
                parseInt(day),
                parseInt(hours),
                parseInt(mins),
                parseInt(secs)
              )
              date.setHours(date.getHours() - 3);
              item.UltimaVisualizacao = date.toLocaleString();
              console.log("Data ajustada:", item.UltimaVisualizacao);
            }
          }
        });
        setListaAtivo(validData);
        setFilteredData(validData);
      }
    } catch (error) {
      console.error("Erro ao obter lista de gateways:", error);
    }
  };

  useEffect(() => {
    obterListaAtivo();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const ativoParam = searchParams.get("ativo");
    console.log("Parâmetro 'ativo' da URL:", ativoParam);

    if (ativoParam) {
      setSelectedAtivo([ativoParam]); // Configura o beacon selecionado com o parâmetro
    }
  }, [location]);

  useEffect(() => {
    if (listaAtivo.length > 0 && selectedAtivo.length > 0) {
      const filtered = listaAtivo.filter((ativo) =>
        selectedAtivo.includes(ativo.Ativo)
      );

      setFilteredData(filtered);

      if (mapRef.current && filtered.length > 0) {
        const validCoordinates = filtered.filter(
          (a) => a.Latitude !== null && a.Longitude !== null
        );

        if (validCoordinates.length > 0) {
          const newBounds = L.latLngBounds(
            validCoordinates.map((a) => [a.Latitude!, a.Longitude!])
          );
          mapRef.current.fitBounds(newBounds);
        }
      }
    }
  }, [listaAtivo, selectedAtivo]);

  const handleFilterChange = (filterType: string, selectedOptions: any) => {
    const selectedValues = selectedOptions
      ? selectedOptions.map((opt: any) => opt.value)
      : [];

    let filtered = listaAtivo;

    if (filterType === "organization") {
      setSelectedOrganization(selectedValues);
      if (selectedValues.length) {
        filtered = filtered.filter((ativo) =>
          selectedValues.includes(ativo.Organizacao)
        );
      }
    }

    if (filterType === "ativo") {
      setSelectedAtivo(selectedValues);
      if (selectedValues.length) {
        filtered = filtered.filter((ativo) =>
          selectedValues.includes(ativo.Ativo)
        );
      }
    }

    setFilteredData(filtered);
  };

  const bounds =
    filteredData.length > 0
      ? L.latLngBounds(
          filteredData
            .filter((a) => a.Latitude !== null && a.Longitude !== null)
            .map((a) => [a.Latitude!, a.Longitude!])
        )
      : null;

  const organizationOptions = Array.from(
    new Set(listaAtivo.map((a) => a.Organizacao))
  ).map((org) => ({ label: org, value: org }));

  const ativoOptions = Array.from(new Set(listaAtivo.map((a) => a.Ativo))).map(
    (atv) => ({ label: atv, value: atv })
  );

  return (
    <main style={{ height: "100vh", width: "100%" }}>
      <h1>Mapa de Ativos</h1>
      <div
        style={{
          display: "flex",
          gap: "10px",
          padding: "10px",
          zIndex: 10000,
        }}
      >
        <Select
          isMulti
          options={organizationOptions}
          onChange={(selected: String) => handleFilterChange("organization", selected)}
          value={organizationOptions.filter((option) =>
            selectedOrganization.includes(option.value)
          )}
          placeholder="Filtrar por Organização"
          isClearable
          menuPortalTarget={document.body}
          classNamePrefix="react-select"
        />
        <Select
          isMulti
          options={ativoOptions}
          onChange={(selected: String) => handleFilterChange("ativo", selected)}
          value={ativoOptions.filter((option) =>
            selectedAtivo.includes(option.value)
          )}
          placeholder="Filtrar por Ativo"
          isClearable
          menuPortalTarget={document.body}
          classNamePrefix="react-select"
        />
      </div>

      <section style={{ marginTop: "20px" }}>
        <h2
          style={{
            textAlign: "center",
            margin: "10px 0",
            color: "goldenrod",
            zIndex: 1,
          }}
        >
          Mapa de Ativos
        </h2>

        <MapContainer
          center={[-22.0, -47.0]}
          zoom={6}
          style={{ height: "80vh", width: "100%" }}
          ref={mapRef} // Adicionado para referência ao mapa
        >
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="OpenStreetMap">
              <TileLayer
                url={osmLayer}
                attribution="&copy; OpenStreetMap contributors"
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name="Satélite Esri">
              <TileLayer url={satelliteLayer} attribution="Tiles © Esri" />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name="Mapa Escuro">
              <TileLayer url={darkLayer} attribution="&copy; CartoDB" />
            </LayersControl.BaseLayer>
          </LayersControl>

          {filteredData.map((ativo, index) => (
            <Marker
              key={index}
              position={[ativo.Latitude!, ativo.Longitude!]}
              icon={L.icon({
                iconUrl:
                  "https://portal.taggen.net/assets/imagens/Beacon_64px.png",
                iconSize: [32, 32],
                iconAnchor: [16, 32],
                popupAnchor: [0, -32],
              })}
            >
              <Popup>
                <strong>Organização:</strong> {ativo.Organizacao}
                <br />
                <strong>Ativo:</strong> {ativo.Ativo}
                <br />
                <strong>IdBeacon:</strong> {ativo.IdBeacon}
                <br />
                <strong>Descrição:</strong> {ativo.Descricao}
                <br />
                <strong>Última Visualização:</strong> {ativo.UltimaVisualizacao}
                <br />
                <strong>Situação:</strong> {ativo.AtivoSituacao}
                <br />
                <strong>Status:</strong>{" "}
                {ativo.StatusGateway === "online" ? "🟢 online" : "🔴 offline"}
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </section>
    </main>
  );
};

export default AtivoMapPage;
